import {
  Component,
  ElementRef,
  OnInit,
  Signal,
  ViewChild,
  ViewEncapsulation,
  effect
} from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, catchError, concatMap, finalize, map, of, take, timeout } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha';

import { InputType } from '@core/enums/input-type';
import { HeaderModalType } from '@core/enums/header-modals';
import { EncryptionService } from '@core/services/encryption.service';
import { AuthService } from '@core/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { IForgotPwRequest, ILoginRequest } from '@core/interfaces/ilogin-request';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'prism-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit {
  @ViewChild('loginCloseButton') loginCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('forgotPasswordCloseButton') forgotPasswordCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('successCloseButton') successCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('maintenanceDialog') maintenanceDialog?: ElementRef<HTMLElement>;

  currentUser: Signal<ILoginResponse | null> = this.authService.currentUserInfo;

  logoUrl = '../../../assets/images/prism-website-logo-final.svg';
  iconImgUrl = 'assets/images/logged-in-user-icon.svg';

  isLoginVisible = false;
  isForgotPwVisible = false;
  successModal = false;
  maintenanceModeModal = false;
  maintenanceMessage = '';

  loginForm!: FormGroup;
  forgotPwForm!: FormGroup;

  InputType = InputType;
  HeaderModalType = HeaderModalType;

  openSideMenu = false;
  isProcessing = false;
  isLoggingIn = false;
  isDropdownOpen = false;
  isSiteOffline = environment.isSiteOffline || false;

  enableRecaptcha = true;
  private readonly recaptchaTimeoutMs = 10000;

  verifyAndSubmit$!: Observable<any>;

  private previouslyFocusedElement: HTMLElement | null = null;

  get isLoggedIn(): boolean {
    return !!this.currentUser();
  }

  get emailAddress() {
    return this.loginForm.get('emailAddress');
  }

  get password() {
    return this.loginForm.get('password');
  }

  get forgotPwEmailAddress() {
    return this.forgotPwForm.get('emailAddress');
  }

  constructor(
    private fb: FormBuilder,
    private encryptionService: EncryptionService,
    private authService: AuthService,
    private recaptchaV3: ReCaptchaV3Service,
    private toastService: ToastService,
    public router: Router
  ) {
    effect(() => {
      this.iconImgUrl = this.currentUser()
        ? 'assets/images/Icon feather-user.svg'
        : 'assets/images/login icon.svg';
    });
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.maxLength(50)]],
      password: ['', [Validators.required, Validators.maxLength(50)]]
    });

    this.forgotPwForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.maxLength(50)]]
    });
  }

  toggleModal(modalType: string): void {
    if (!this.anyModalOpen()) {
      this.previouslyFocusedElement = document.activeElement as HTMLElement;
    }

    this.isLoginVisible = modalType === HeaderModalType.Login;
    this.isForgotPwVisible = modalType === HeaderModalType.ForgotPw;
    this.successModal = modalType === HeaderModalType.ForgotPwConfirmation;
    this.maintenanceModeModal = false;

    this.focusActiveModal();
  }

  handleCancel(): void {
  this.isLoginVisible = false;
  this.isForgotPwVisible = false;
  this.successModal = false;
  this.maintenanceModeModal = false;

  this.loginForm.reset();
  this.forgotPwForm.reset();

  this.loginForm.markAsPristine();
  this.loginForm.markAsUntouched();

  this.forgotPwForm.markAsPristine();
  this.forgotPwForm.markAsUntouched();

  this.restorePreviousFocus();
}

  onDialogKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleCancel();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements(dialog);

    if (!focusableElements.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  submit(): void {
    // Ignore repeat taps while a login is in flight (the reCAPTCHA wait can take up to 10s)
    if (this.isLoggingIn) return;
    this.isLoggingIn = true;

    const loginPayload: ILoginRequest = this.loginForm.value;
    loginPayload.password = this.encryptionService.encryptPassword(this.password?.value);

    if (this.enableRecaptcha) {
      this.verifyAndSubmit$ = this.getRecaptchaToken().pipe(
        concatMap((token: string | null) =>
          // reCAPTCHA timed out (seen on Safari) — log in without it rather than hang
          !token
            ? this.authService.login(loginPayload, 'Login Successful')
            : this.authService.siteVerify(token).pipe(
                concatMap((valid: boolean) =>
                  valid
                    ? this.authService.login(loginPayload, 'Login Successful').pipe(
                        map((res: ILoginResponse) => res)
                      )
                    : of(valid)
                ),
                catchError(() => of(false))
              )
        )
      );
    } else {
      this.verifyAndSubmit$ = this.authService.login(loginPayload, 'Login Successful').pipe(
        map((res: ILoginResponse) => res)
      );
    }

    this.verifyAndSubmit$.pipe(take(1), finalize(() => (this.isLoggingIn = false))).subscribe({
      next: (res: ILoginResponse) => {
        if (res) {
          this.isLoginVisible = false;
          this.authService.captureUserInfo(res);
          this.router.navigateByUrl('dashboard');
          this.restorePreviousFocus();
        }
      },
      error: (err) => {
        if (err?.status === 403 && err?.error?.message?.includes('maintenance mode')) {
          this.isLoginVisible = false;
          this.maintenanceMessage =
            err?.error?.message ||
            'The system is currently in maintenance mode. Please check back later.';

          this.maintenanceModeModal = true;
          this.focusActiveModal();
        }
      }
    });
  }

  submitForgotPw(): void {
    const payload: IForgotPwRequest = this.forgotPwForm.value;

    if (this.enableRecaptcha) {
      this.verifyAndSubmit$ = this.getRecaptchaToken().pipe(
        concatMap((token: string | null) =>
          !token
            ? this.authService.forgotPassword(
                payload,
                'Please check your email for reset password instructions.'
              )
            : this.authService.siteVerify(token).pipe(
                concatMap((valid: boolean) =>
                  valid
                    ? this.authService.forgotPassword(
                        payload,
                        'Please check your email for reset password instructions.'
                      )
                    : of(false)
                )
              )
        ),
        catchError(() => of(false))
      );
    } else {
      this.verifyAndSubmit$ = this.authService
        .forgotPassword(payload, 'Please check your email for reset password instructions.')
        .pipe(catchError(() => of(false)));
    }

    this.isProcessing = true;

    this.verifyAndSubmit$.pipe(take(1)).subscribe((success: boolean) => {
      this.isProcessing = false;

      if (success) {
        this.isForgotPwVisible = false;
        this.successModal = true;
        this.forgotPwForm.reset();
        this.focusActiveModal();
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }

  /** Emits the reCAPTCHA token, or null if Google doesn't respond in time. */
  private getRecaptchaToken(): Observable<string | null> {
    return this.recaptchaV3.execute('handleSubmit').pipe(
      timeout(this.recaptchaTimeoutMs),
      catchError((err) => {
        console.warn('[reCAPTCHA] execute failed or timed out; continuing without it', err);
        return of(null);
      })
    );
  }

  click(): void {
    this.openSideMenu = !this.openSideMenu;
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  private focusActiveModal(): void {
    setTimeout(() => {
      if (this.isLoginVisible) {
        this.loginCloseButton?.nativeElement.focus();
        return;
      }

      if (this.isForgotPwVisible) {
        this.forgotPasswordCloseButton?.nativeElement.focus();
        return;
      }

      if (this.successModal) {
        this.successCloseButton?.nativeElement.focus();
        return;
      }

      if (this.maintenanceModeModal) {
        this.maintenanceDialog?.nativeElement.focus();
      }
    });
  }

  private anyModalOpen(): boolean {
    return (
      this.isLoginVisible ||
      this.isForgotPwVisible ||
      this.successModal ||
      this.maintenanceModeModal
    );
  }

  private restorePreviousFocus(): void {
    setTimeout(() => {
      this.previouslyFocusedElement?.focus();
      this.previouslyFocusedElement = null;
    });
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
      (element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden')
    );
  }
}